// Knowledge — AI-powered Q&A across the firm's deal pipeline and investment themes.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  askKnowledge, createTheme, deleteTheme, draftTheme, fetchKnowledgeChats,
  fetchThemes, generateThemes, patchKnowledgeChat, patchTheme, setChatFeedback,
} from "../api";
import { useAppData, useMe } from "../store";
import type { Deal, Theme } from "../types";
import { Btn, Chip, TextInput } from "../components/Primitives";
import { Ic } from "../components/Icon";
import { Md } from "../components/Md";
import { Logo } from "../components/Logo";

// ── Types ────────────────────────────────────────────────────────────────────

interface KbSource {
  type: "deal" | "note" | "theme";
  title: string;
  slug?: string | null;   // deal slug — present for validated deal/note sources
  score?: number | null;
  notes?: number | null;
  who?: string | null;
  date?: string | null;
}

interface KbChat {
  id: string;
  question: string;
  answer: string[];
  sources: KbSource[];
  when: string;
  timeGroup: string;
  author: string;
  saved: boolean;
  feedback?: "up" | "down" | null;   // this user's rating, from the server
  threadId?: string;
  _loading?: boolean;
}

interface KbDeal {
  name: string;
  domain: string;
  score: number;
}

interface KbTheme {
  id: string;
  title: string;
  sector: string;
  sectorColor: string;
  weight: number;
  notes: number;
  lastUpdate: string;
  conviction: number;
  owners: string[];
  momentum: string;
  summary: string;
  keyInsight: string;
  deals: KbDeal[];
}

// ── Static knowledge library ──────────────────────────────────────────────────

// ── Server themes → display cards ─────────────────────────────────────────────
// Real themes come from /api/knowledge/themes (partner-written or agent-drafted
// from the firm's own deals). The decorative card fields are derived here.

const SECTOR_COLORS = ["#2a3a52", "#4d6a3e", "#7a5a2a", "#3a4859", "#8a3f3a", "#5a564c", "#5e4a6e"];

function sectorColor(s: string): string {
  let h = 0;
  for (const c of s || "—") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return SECTOR_COLORS[h % SECTOR_COLORS.length];
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function themeToCard(t: Theme, dealsBySlug: Map<string, Deal>): KbTheme {
  const deals = t.dealSlugs
    .map((s) => dealsBySlug.get(s))
    .filter((d): d is Deal => Boolean(d))
    .map((d) => ({ name: d.co, domain: d.domain ?? "", score: d.score ?? 0 }));
  return {
    id: t.id,
    title: t.title,
    sector: t.sector || "—",
    sectorColor: sectorColor(t.sector || "—"),
    weight: t.conviction >= 75 ? 4 : 2,
    notes: t.dealSlugs.length,
    lastUpdate: relTime(t.updatedAt),
    conviction: Math.max(1, Math.min(5, Math.round(t.conviction / 20))),
    owners: t.author ? [t.author] : [],
    momentum: "Tracking",
    summary: t.summary,
    keyInsight: t.keyInsight || "",
    deals,
  };
}

const SECTOR_BG: Record<string, string> = {
  "AI Infrastructure": "rgba(42,58,82,0.07)",
  "Climate Tech":      "rgba(77,106,62,0.07)",
  "Nuclear Energy":    "rgba(90,74,58,0.07)",
  "Health":            "rgba(122,90,42,0.07)",
  "Fintech":           "rgba(58,72,89,0.07)",
  "Developer tools":   "rgba(28,74,58,0.07)",
  "Bio":               "rgba(138,63,58,0.07)",
  "Hardware":          "rgba(90,106,74,0.07)",
  "Defense":           "rgba(58,74,58,0.07)",
};

const TEAM_NAMES: Record<string, string> = {
  LS: "Lucas Schneider",
  MS: "Mateo Santos",
  RT: "Rohini Tan",
  AB: "Aki Brodeur",
  KH: "Kira Hadley",
};

const DEMO_QUESTIONS = [
  "Which deals have the highest scores right now?",
  "What patterns show up across the deals we passed on?",
  "Summarize our most active sector this quarter.",
  "Which active deals are still unassigned?",
  "What stood out in our most recent inbound deals?",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// Answer ratings (Helpful / Off-base) are stored per user in the database, so
// each member sees their own ratings across reloads and devices.
type Rating = "up" | "down";

// "Generate themes" with an optional steer (sector/stage focus) behind a chevron.
function GenerateThemesButton({ generating, onGenerate }: {
  generating: boolean; onGenerate: (guidance?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const run = (guidance?: string) => { setOpen(false); setText(""); onGenerate(guidance); };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button
        className="btn ghost sm"
        disabled={generating}
        onClick={() => setOpen((o) => !o)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <Ic name="sparkle" size={13} />
        {generating ? "Generating…" : "Generate themes"}
        <Ic name="chevDown" size={11} />
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", width: 320,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 10, boxShadow: "var(--shadow-pop)", padding: 12, zIndex: 80,
        }}>
          <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ink-3)", marginBottom: 8 }}>
            Tailor the themes (optional)
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="e.g. focus on fintech and European seed deals"
            className="input"
            style={{ width: "100%", resize: "vertical", padding: "8px 10px", fontFamily: "var(--sans)", fontSize: 13 }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
            <Btn size="sm" kind="ghost" onClick={() => run()}>From all deals</Btn>
            <Btn size="sm" kind="primary" ico="sparkle" onClick={() => run(text.trim() || undefined)}>
              Generate
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Conviction dots ───────────────────────────────────────────────────────────

function ConvictionDots({ value, color }: { value: number; color: string }) {
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: 3, background: i <= value ? color : "var(--raised)", border: i <= value ? "none" : "1px solid var(--border)" }} />
      ))}
    </div>
  );
}

// ── Partner avatar ────────────────────────────────────────────────────────────

function PartnerAvatar({ id }: { id: string }) {
  const colors: Record<string,string> = { LS:"#2a3a52", MS:"#4d6a3e", RT:"#7a5a2a", AB:"#3a4859", KH:"#8a3f3a" };
  return (
    <span title={TEAM_NAMES[id] || id} style={{ width: 20, height: 20, borderRadius: 10, background: colors[id] || "#aaa", color: "#fff", display: "inline-grid", placeItems: "center", fontSize: 9, fontWeight: 600, fontFamily: "var(--sans)", marginLeft: -5, border: "2px solid var(--bg)" }}>
      {id}
    </span>
  );
}

// ── Momentum badge ────────────────────────────────────────────────────────────

function MomentumBadge({ value, color }: { value: string; color: string }) {
  const arrow = value === "Accelerating" ? "↑" : value === "Decelerating" ? "↓" : "→";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 99, fontSize: 10.5, fontWeight: 500, background: `${color}18`, color }}>
      {arrow} {value}
    </span>
  );
}

// ── SpinnerIcon ───────────────────────────────────────────────────────────────

function SpinnerIcon({ size = 14 }: { size?: number }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    let angle = 0;
    const id = setInterval(() => {
      angle = (angle + 12) % 360;
      if (ref.current) ref.current.style.transform = `rotate(${angle}deg)`;
    }, 50);
    return () => clearInterval(id);
  }, []);
  return (
    <svg ref={ref} viewBox="0 0 24 24" width={size} height={size}
      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.2-8.5" />
    </svg>
  );
}

// ── Theme card ────────────────────────────────────────────────────────────────

function ThemeCard({ t, onAsk, draft = false, generated = false, canEdit = false, onPublish, onDelete, onEdit }: {
  t: KbTheme; onAsk: (q: string) => void;
  draft?: boolean; generated?: boolean; canEdit?: boolean;
  onPublish?: () => void; onDelete?: () => void; onEdit?: () => void;
}) {
  const big = t.weight >= 4;
  const color = t.sectorColor;
  const bgAccent = SECTOR_BG[t.sector] || "rgba(0,0,0,0.04)";

  return (
    <div style={{
      breakInside: "avoid",
      marginBottom: 14,
      border: "1px solid var(--border)",
      borderRadius: 12,
      overflow: "hidden",
      cursor: "pointer",
      background: "var(--surface)",
    }}>
      {/* Color header strip */}
      <div style={{ background: bgAccent, borderBottom: "1px solid var(--border)", padding: big ? "16px 20px 14px" : "12px 16px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.09em", color }}>
            {t.sector}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {draft ? (
              <span style={{ padding: "2px 7px", borderRadius: 99, fontSize: 9.5, fontWeight: 600, background: "var(--warn-soft)", color: "var(--warn)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {generated ? "AI draft" : "Draft"}
              </span>
            ) : generated ? (
              <span title="Drafted by Prism from your deals" style={{ padding: "2px 7px", borderRadius: 99, fontSize: 9.5, fontWeight: 600, background: "var(--raised)", color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                AI
              </span>
            ) : null}
            {big && !draft && (
              <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9.5, color, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block" }} />
                High conviction
              </span>
            )}
          </div>
        </div>

        <div style={{
          fontFamily: big ? "var(--serif, Georgia)" : "var(--sans)",
          fontSize: big ? 20 : 14,
          fontWeight: big ? 500 : 600,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          color: "var(--ink)",
        }}>{t.title}</div>

        {/* Conviction row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
          <ConvictionDots value={t.conviction} color={color} />
          <span style={{ fontSize: 10.5, color: "var(--ink-3)" }}>Conviction</span>
          <span style={{ flex: 1 }} />
          <div style={{ display: "flex", marginLeft: 5 }}>
            {t.owners.map(o => <PartnerAvatar key={o} id={o} />)}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: big ? "16px 20px" : "12px 16px" }}>
        <div style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.65, margin: "0 0 14px" }}>
          <Md text={t.summary} />
        </div>

        {/* Key insight callout */}
        <div style={{ padding: "10px 14px", background: bgAccent, borderLeft: `3px solid ${color}`, borderRadius: "0 6px 6px 0", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color, marginBottom: 4 }}>Key insight</div>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, margin: 0, color: "var(--ink-2)", fontStyle: "italic" }}>"<Md text={t.keyInsight} />"</p>
        </div>

        {/* Related deals */}
        {t.deals.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink-3)", marginBottom: 8 }}>
              Related deals · {t.deals.length}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {t.deals.map(d => (
                <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 7 }}>
                  <Logo domain={d.domain} co={d.name} size={24} radius={5} />
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: 500 }}>{d.name}</span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 700, color, background: `${color}14`, padding: "1px 6px", borderRadius: 4 }}>{d.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <Ic name="note" size={11} />
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{t.notes} deals</span>
          <span style={{ fontSize: 11, color: "var(--ink-4)" }}>·</span>
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{t.lastUpdate}</span>
          <span style={{ flex: 1 }} />
          {draft && canEdit ? (
            <>
              <button onClick={e => { e.stopPropagation(); onDelete?.(); }}
                style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", fontSize: 11, color: "var(--ink-3)", cursor: "pointer", fontFamily: "var(--sans)" }}>
                Discard
              </button>
              <button onClick={e => { e.stopPropagation(); onPublish?.(); }}
                style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid var(--ink)", background: "var(--ink)", fontSize: 11, color: "var(--bg)", cursor: "pointer", fontFamily: "var(--sans)", display: "flex", alignItems: "center", gap: 4 }}>
                <Ic name="check" size={10} /> Publish
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button onClick={e => { e.stopPropagation(); onEdit?.(); }}
                  style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", fontSize: 11, color: "var(--ink-3)", cursor: "pointer", fontFamily: "var(--sans)" }}>
                  Edit
                </button>
              )}
              <button onClick={e => { e.stopPropagation(); onAsk(`Tell me more about "${t.title}"`); }}
                style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", fontSize: 11, color: "var(--ink-3)", cursor: "pointer", fontFamily: "var(--sans)", display: "flex", alignItems: "center", gap: 4 }}>
                <Ic name="sparkle" size={10} /> Ask AI
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Library view ──────────────────────────────────────────────────────────────

interface LibraryProps {
  themes: Theme[];
  deals: Deal[];
  isAdmin: boolean;
  generating: boolean;
  /** Theme just saved from the modal — reset the filter, scroll it into view
   *  and flash it (the conviction sort can land it mid-grid otherwise). */
  highlight?: string | null;
  onAsk: (q: string) => void;
  onGenerate: (guidance?: string) => void;
  onPublish: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onEdit: (t: Theme) => void;
}

function LibraryView({ themes, deals, isAdmin, generating, highlight, onAsk, onGenerate, onPublish, onDelete, onNew, onEdit }: LibraryProps) {
  const [sector, setSector] = useState("All");
  const highlightRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!highlight) return;
    setSector("All");
    // Wait a frame so the un-filtered grid has rendered before scrolling.
    const t = window.setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [highlight]);
  const dealsBySlug = useMemo(() => {
    const m = new Map<string, Deal>();
    deals.forEach(d => m.set(d.id, d));
    return m;
  }, [deals]);

  const [sort, setSort] = useState<"conviction" | "recent">("conviction");
  const sectors = ["All", ...Array.from(new Set(themes.map(t => t.sector).filter(Boolean)))];
  const visible = themes.filter(t => sector === "All" || t.sector === sector);
  const sorted = [...visible].sort((a, b) => {
    // Drafts float to the top so they get reviewed; then the chosen sort.
    if ((a.status === "draft") !== (b.status === "draft")) return a.status === "draft" ? -1 : 1;
    if (sort === "recent") return (b.updatedAt || "").localeCompare(a.updatedAt || "");
    return b.conviction - a.conviction;
  });

  if (themes.length === 0) {
    return (
      <div style={{ padding: "60px 28px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "inline-grid", placeItems: "center", width: 46, height: 46, borderRadius: 23, background: "var(--surface)", border: "1px solid var(--border)", marginBottom: 16, color: "var(--ink-3)" }}>
          <Ic name="knowledge" size={20} />
        </div>
        <h2 style={{ fontFamily: "var(--serif, Georgia)", fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>No themes yet</h2>
        <p style={{ color: "var(--ink-2)", fontSize: 13.5, lineHeight: 1.6, margin: "0 0 20px" }}>
          Themes are the firm's recurring theses — written by a partner or drafted
          by Prism from your own deals, notes, and memos. {isAdmin
            ? "Let Prism read your pipeline and propose a first set."
            : "Ask an admin to generate the first set."}
        </p>
        {isAdmin && (
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <Btn ico="sparkle" kind="primary" disabled={generating} onClick={() => onGenerate()}>
              {generating ? "Reading your pipeline…" : "Generate themes from my deals"}
            </Btn>
            <Btn ico="plus" onClick={onNew}>Write one with AI</Btn>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)" }}>Filter</span>
        {sectors.map(s => <Chip key={s} on={s === sector} onClick={() => setSector(s)}>{s}</Chip>)}
        <span style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", marginLeft: 12 }}>Sort</span>
        <Chip on={sort === "conviction"} onClick={() => setSort("conviction")}>Conviction</Chip>
        <Chip on={sort === "recent"} onClick={() => setSort("recent")}>Most recent</Chip>
        <span style={{ flex: 1 }} />
        {isAdmin && (
          <GenerateThemesButton generating={generating} onGenerate={onGenerate} />
        )}
      </div>

      <div style={{ columns: 3, columnGap: 14 }}>
        {sorted.map(t => (
          <div
            key={t.id}
            ref={t.id === highlight ? highlightRef : undefined}
            style={{
              breakInside: "avoid",
              ...(t.id === highlight ? { outline: "2px solid var(--accent)", outlineOffset: 2, borderRadius: 12 } : {}),
            }}
          >
            <ThemeCard
              t={themeToCard(t, dealsBySlug)}
              onAsk={onAsk}
              draft={t.status === "draft"}
              generated={t.generated}
              canEdit={isAdmin}
              onPublish={() => onPublish(t.id)}
              onDelete={() => onDelete(t.id)}
              onEdit={() => onEdit(t)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Chat sidebar row ──────────────────────────────────────────────────────────

function ChatRow({ chat, active, onPick }: { chat: KbChat; active: boolean; onPick: () => void }) {
  return (
    <div onClick={onPick} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 2, background: active ? "var(--surface)" : "transparent", boxShadow: active ? "0 0 0 1px var(--border)" : "none", opacity: chat._loading ? 0.7 : 1 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <span style={{ marginTop: 2, color: "var(--ink-3)", flexShrink: 0 }}>
          {chat._loading ? <SpinnerIcon size={11} /> : chat.saved ? <Ic name="bookmark" size={11} /> : <Ic name="chat" size={11} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: active ? 500 : 400, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } as React.CSSProperties}>{chat.question}</div>
          <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 2 }}>{chat._loading ? "Thinking…" : chat.when}</div>
        </div>
      </div>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingAnswer({ question }: { question: string }) {
  return (
    <>
      <style>{`@keyframes kb-pulse{0%,100%{opacity:.45}50%{opacity:1}}`}</style>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", marginBottom: 6 }}>Just now · asked by you</div>
        <h2 style={{ fontFamily: "var(--serif, Georgia)", fontSize: 24, fontWeight: 500, letterSpacing: "-0.015em", margin: 0, lineHeight: 1.2 }}>{question}</h2>
      </div>
      <div style={{ padding: "22px 26px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 18, height: 18, borderRadius: 9, background: "var(--ink)", color: "var(--bg)", display: "grid", placeItems: "center" }}><Ic name="sparkle" size={10} /></div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Answer</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 5 }}><SpinnerIcon size={11} /> Searching across deals &amp; notes…</span>
        </div>
        {[82, 65, 72, 55].map((w, i) => (
          <div key={i} style={{ height: 13, borderRadius: 4, background: "var(--raised)", width: `${w}%`, marginBottom: 10, animation: `kb-pulse 1.5s ease-in-out ${i * 0.15}s infinite` }} />
        ))}
      </div>
    </>
  );
}

// ── Answer view ───────────────────────────────────────────────────────────────

function AnswerView({ chat, onSave, onFollowup, onOpenDeal, onPromote, onRate }: { chat: KbChat; onSave: (id: string) => void; onFollowup: (q: string, threadId?: string) => void; onOpenDeal: (id: string) => void; onPromote: () => void; onRate: (id: string, rating: Rating | null) => void }) {
  const [fu, setFu] = useState("");
  const rating = chat.feedback ?? null;
  const rate = (v: Rating) => onRate(chat.id, rating === v ? null : v);
  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 22 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", marginBottom: 6 }}>{chat.when} · asked by {chat.author}</div>
          <h2 style={{ fontFamily: "var(--serif, Georgia)", fontSize: 24, fontWeight: 500, letterSpacing: "-0.015em", margin: 0, lineHeight: 1.2 }}>{chat.question}</h2>
        </div>
        <Btn size="sm" ico="bookmark" kind={chat.saved ? "accent" : "default"} onClick={() => onSave(chat.id)}>{chat.saved ? "Saved" : "Save"}</Btn>
      </div>

      <div style={{ padding: "22px 26px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <div style={{ width: 18, height: 18, borderRadius: 9, background: "var(--ink)", color: "var(--bg)", display: "grid", placeItems: "center" }}><Ic name="sparkle" size={10} /></div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Answer</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>Across {chat.sources.length} sources</span>
        </div>
        {chat.answer.map((p, i) => (
          <div key={i} style={{ fontSize: 14, lineHeight: 1.7, color: "var(--ink-2)", margin: "0 0 10px" }}><Md text={p} /></div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 14, alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 14 }}>
          <Btn size="sm" ico="check" kind={rating === "up" ? "accent" : "default"} onClick={() => rate("up")}>Helpful</Btn>
          <Btn size="sm" ico="x" kind={rating === "down" ? "accent" : "ghost"} onClick={() => rate("down")}>Off-base</Btn>
          {rating && <span style={{ fontSize: 11, color: "var(--ink-3)" }}>Thanks — noted</span>}
          <span style={{ flex: 1 }} />
          <Btn size="sm" kind="ghost" ico="note" onClick={onPromote}>Promote to theme</Btn>
        </div>
      </div>

      {/* Sources */}
      {chat.sources.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", marginBottom: 10 }}>Sources · {chat.sources.length}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {chat.sources.map((src, i) => {
              const clickable = Boolean(src.slug);
              return (
                <div key={i}
                  onClick={() => src.slug && onOpenDeal(src.slug)}
                  title={clickable ? "Open deal" : undefined}
                  style={{ padding: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, cursor: clickable ? "pointer" : "default", transition: "border-color .12s" }}
                  onMouseEnter={(e) => { if (clickable) e.currentTarget.style.borderColor = "var(--border-strong)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 9.5, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>[{i + 1}]</span>
                    <span style={{ padding: "1px 7px", borderRadius: 4, background: "var(--raised)", fontSize: 10.5, color: "var(--ink-2)", fontWeight: 500 }}>{src.type}</span>
                    <span style={{ flex: 1 }} />
                    {src.score != null && <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 700 }}>{src.score}</span>}
                    {clickable && <Ic name="ext" size={11} />}
                  </div>
                  <div style={{ fontWeight: 500, fontSize: 13, letterSpacing: "-0.005em", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{src.title}</div>
                  {(src.notes != null || src.who) && (
                    <div style={{ color: "var(--ink-3)", fontSize: 11, marginTop: 4 }}>{src.notes != null ? `${src.notes} notes` : `${src.who} · ${src.date}`}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Follow-up */}
      <div style={{ padding: "12px 16px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
        <Ic name="chat" size={14} />
        <input value={fu} onChange={e => setFu(e.target.value)} placeholder="Ask a follow-up…"
          style={{ flex: 1, border: 0, outline: 0, fontSize: 13.5, background: "transparent", fontFamily: "var(--sans)" }}
          onKeyDown={e => { if (e.key === "Enter" && fu.trim()) { onFollowup(fu, chat.threadId); setFu(""); } }} />
        <span className="kbd">↵</span>
      </div>

      <div>
        <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", marginBottom: 8 }}>Suggested follow-ups</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["What are the most-cited notes?", "Compare to last quarter", "Which partners disagree?"].map(q => (
            <button key={q} onClick={() => onFollowup(q, chat.threadId)} style={{ padding: "5px 11px", borderRadius: 999, background: "var(--surface)", border: "1px solid var(--border)", fontSize: 12, color: "var(--ink-2)", cursor: "pointer", fontFamily: "var(--sans)" }}>{q}</button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Chat view ─────────────────────────────────────────────────────────────────

function ChatView({ chats, activeId, onPick, onSave, onFollowup, onOpenDeal, onPromote, onRate }: {
  chats: KbChat[]; activeId: string | null;
  onPick: (id: string) => void; onSave: (id: string) => void; onFollowup: (q: string, threadId?: string) => void; onOpenDeal: (id: string) => void; onPromote: (chat: KbChat) => void; onRate: (id: string, rating: Rating | null) => void;
}) {
  const cur = chats.find(c => c.id === activeId) ?? chats[0] ?? null;
  const groups: Record<string, KbChat[]> = {};
  for (const c of chats.filter(c => !c._loading)) (groups[c.timeGroup] ??= []).push(c);
  const loading = chats.find(c => c._loading);
  const ORDER = ["Today", "Yesterday", "Earlier this week", "Earlier"];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", flex: 1, minHeight: 0 }}>
      <div style={{ borderRight: "1px solid var(--border)", overflowY: "auto", background: "var(--bg)" }}>
        <div style={{ padding: "16px 12px 8px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", padding: "0 8px 8px" }}>Saved</div>
          {chats.filter(c => c.saved).map(c => <ChatRow key={c.id} chat={c} active={c.id === cur?.id} onPick={() => onPick(c.id)} />)}
          {!chats.some(c => c.saved) && <div style={{ padding: "4px 10px", fontSize: 11, color: "var(--ink-4)" }}>No saved answers yet.</div>}
        </div>
        {loading && (
          <div style={{ padding: "12px 12px 0" }}>
            <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", padding: "0 8px 8px" }}>Today</div>
            <ChatRow chat={loading} active={loading.id === cur?.id} onPick={() => onPick(loading.id)} />
          </div>
        )}
        {ORDER.map(g => groups[g] && (
          <div key={g} style={{ padding: "12px 12px 0" }}>
            <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--ink-3)", padding: "0 8px 8px" }}>{g}</div>
            {groups[g].map(c => <ChatRow key={c.id} chat={c} active={c.id === cur?.id} onPick={() => onPick(c.id)} />)}
          </div>
        ))}
      </div>
      <div style={{ overflowY: "auto", padding: "26px 36px 80px" }}>
        {cur?._loading ? <LoadingAnswer question={cur.question} /> : cur ? <AnswerView chat={cur} onSave={onSave} onFollowup={onFollowup} onOpenDeal={onOpenDeal} onPromote={() => onPromote(cur)} onRate={onRate} /> : null}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── Theme create / edit modal (AI-assisted) ───────────────────────────────────

function ThemeModal({ theme, deals, promote, onClose, onSaved }: {
  theme: Theme | "new";
  deals: Deal[];
  promote?: Partial<Theme> | null;   // seed a NEW theme from a Knowledge answer
  onClose: () => void;
  onSaved: (t: Theme) => void;
}) {
  const editing = theme !== "new";
  const seed = editing ? (theme as Theme) : null;
  const init = seed ?? promote ?? null;   // field defaults (edit target, or promote seed)
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState(init?.title ?? "");
  const [sector, setSector] = useState(init?.sector ?? "");
  const [summary, setSummary] = useState(init?.summary ?? "");
  const [keyInsight, setKeyInsight] = useState(init?.keyInsight ?? "");
  const [conviction, setConviction] = useState(init?.conviction ?? 70);
  const [slugs, setSlugs] = useState<string[]>(init?.dealSlugs ?? []);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dealName = (slug: string) => deals.find((d) => d.id === slug)?.co ?? slug;

  const runDraft = async () => {
    if (!prompt.trim() || drafting) return;
    setDrafting(true); setError(null);
    try {
      const d = await draftTheme(prompt.trim());
      setTitle(d.title); setSector(d.sector); setSummary(d.summary);
      setKeyInsight(d.keyInsight); setConviction(d.conviction); setSlugs(d.dealSlugs);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true); setError(null);
    const body = { title: title.trim(), sector, summary, keyInsight, conviction, dealSlugs: slugs };
    try {
      const saved = editing
        ? await patchTheme((seed as Theme).id, body)
        : await createTheme({ ...body, status: "published" });
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(String((e as Error).message || e));
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(20,18,15,0.4)", zIndex: 200, display: "grid", placeItems: "center", padding: 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxHeight: "90vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ fontFamily: "var(--serif, Georgia)", fontSize: 20, fontWeight: 500, margin: 0 }}>
            {editing ? "Edit theme" : "New theme"}
          </h2>
          <span style={{ flex: 1 }} />
          <span onClick={onClose} style={{ cursor: "pointer", color: "var(--ink-3)" }}><Ic name="x" size={16} /></span>
        </div>

        {!editing && (
          <div style={{ marginBottom: 18, padding: 14, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ink-3)", marginBottom: 8 }}>Draft with AI</div>
            <div style={{ display: "flex", gap: 8 }}>
              <TextInput placeholder="What's the theme about? e.g. 'why we back voice agents'" value={prompt} onChange={setPrompt} style={{ flex: 1 }} />
              <Btn kind="primary" ico="sparkle" disabled={drafting || !prompt.trim()} onClick={runDraft}>
                {drafting ? "Drafting…" : "Draft"}
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 6 }}>
              Prism reads your deals and fills the fields below — edit anything, then publish.
            </div>
          </div>
        )}

        <Field label="Title"><TextInput value={title} onChange={setTitle} placeholder="Theme title" /></Field>
        <Field label="Sector"><TextInput value={sector} onChange={setSector} placeholder="e.g. AI Infrastructure" /></Field>
        <Field label="Summary">
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4}
            placeholder="The thesis and the evidence." className="input"
            style={{ width: "100%", resize: "vertical", padding: "8px 10px", fontFamily: "var(--sans)", fontSize: 13 }} />
        </Field>
        <Field label="Key insight"><TextInput value={keyInsight} onChange={setKeyInsight} placeholder="The one-sentence crux" /></Field>
        <Field label={`Conviction · ${conviction}`}>
          <input type="range" min={0} max={100} value={conviction} onChange={(e) => setConviction(+e.target.value)} style={{ width: "100%" }} />
        </Field>
        {slugs.length > 0 && (
          <Field label={`Related deals · ${slugs.length}`}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {slugs.map((s) => (
                <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 999, fontSize: 12 }}>
                  {dealName(s)}
                  <span onClick={() => setSlugs(slugs.filter((x) => x !== s))} style={{ cursor: "pointer", color: "var(--ink-4)" }}><Ic name="x" size={9} /></span>
                </span>
              ))}
            </div>
          </Field>
        )}

        {error && <div style={{ color: "var(--neg)", background: "var(--neg-soft)", padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginTop: 8 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "center", justifyContent: "flex-end" }}>
          {!title.trim() && (
            <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
              Add a title to {editing ? "save" : "publish"}
            </span>
          )}
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={saving || !title.trim()} onClick={save}>
            {saving ? "Saving…" : editing ? "Save" : "Publish theme"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 500, display: "block", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

export function KnowledgeScreen({ onOpenDeal }: { onOpenDeal: (id: string) => void }) {
  const me = useMe();
  const { data } = useAppData();
  const isAdmin = me?.role === "Admin";
  const deals = useMemo(
    () => [...(data?.deals ?? []), ...(data?.archive ?? [])],
    [data],
  );
  const [mode, setMode] = useState<"library" | "chat">("library");
  const [q, setQ] = useState("");
  const [chats, setChats] = useState<KbChat[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchKnowledgeChats<KbChat>()
      .then((d) => { setChats(d); if (d[0]) setActiveId(d[0].id); })
      .catch(() => {});
    fetchThemes().then(setThemes).catch(() => {});
  }, []);

  const submit = async (text: string, threadId?: string) => {
    const lid = "_loading_" + Date.now();
    const placeholder: KbChat = { id: lid, question: text, answer: [], sources: [], when: "Just now", timeGroup: "Today", author: me?.initials ?? "", saved: false, _loading: true };
    setQ(""); setMode("chat"); setLoading(true);
    setChats(prev => [placeholder, ...prev]);
    setActiveId(lid);
    try {
      const chat = await askKnowledge<KbChat>(text, threadId);
      setChats(prev => [chat, ...prev.filter(c => c.id !== lid)]);
      setActiveId(chat.id);
    } catch {
      setChats(prev => prev.filter(c => c.id !== lid));
    } finally {
      setLoading(false);
    }
  };

  const onGenerate = async (guidance?: string) => {
    setGenerating(true);
    try {
      const drafts = await generateThemes(guidance);
      setThemes(prev => [...drafts, ...prev]);
    } finally {
      setGenerating(false);
    }
  };

  const onPublish = async (id: string) => {
    const t = await patchTheme(id, { status: "published" });
    setThemes(prev => prev.map(x => x.id === id ? t : x));
  };

  const onDeleteTheme = async (id: string) => {
    await deleteTheme(id).catch(() => {});
    setThemes(prev => prev.filter(x => x.id !== id));
  };

  const [themeModal, setThemeModal] = useState<Theme | "new" | null>(null);
  const [themeSeed, setThemeSeed] = useState<Partial<Theme> | null>(null);
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const highlightTimer = useRef<number | null>(null);
  const onThemeSaved = (t: Theme) => {
    setThemes(prev =>
      prev.some(x => x.id === t.id) ? prev.map(x => x.id === t.id ? t : x) : [t, ...prev]);
    setJustSaved(t.id);
    if (highlightTimer.current != null) window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => setJustSaved(null), 3500);
  };

  // Promote a Q&A answer into a new theme: pre-seed the modal from it.
  const onPromote = (chat: KbChat) => {
    setThemeSeed({
      title: chat.question.replace(/\?+\s*$/, ""),
      summary: chat.answer.join("\n\n"),
      dealSlugs: chat.sources.map(s => s.slug).filter((s): s is string => Boolean(s)),
    });
    setThemeModal("new");
  };
  const closeThemeModal = () => { setThemeModal(null); setThemeSeed(null); };

  const save = async (id: string) => {
    const chat = chats.find(c => c.id === id);
    if (!chat) return;
    const saved = !chat.saved;
    setChats(prev => prev.map(c => c.id === id ? { ...c, saved } : c));
    await patchKnowledgeChat<KbChat>(id, saved).catch(() => {});
  };

  // Per-user Helpful/Off-base rating — optimistic local update, persisted server-side.
  const rate = async (id: string, rating: Rating | null) => {
    setChats(prev => prev.map(c => c.id === id ? { ...c, feedback: rating } : c));
    await setChatFeedback(id, rating).catch(() => {});
  };

  const hasQuery = q.trim() && !loading;

  return (
    <div className="page">
      <div className="topbar">
        <h1>Knowledge</h1>
        <span className="crumb">
          · {themes.length} themes
          {themes.some(t => t.status === "draft") &&
            ` · ${themes.filter(t => t.status === "draft").length} drafts`}
        </span>
        <span className="grow" />
        {isAdmin && (
          <Btn ico="plus" size="sm" onClick={() => { setThemeSeed(null); setThemeModal("new"); }}>New theme</Btn>
        )}
        {isAdmin && (
          <GenerateThemesButton generating={generating} onGenerate={onGenerate} />
        )}
      </div>

      {/* Chat bar */}
      <div style={{ padding: "18px 28px 14px", background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px 12px 16px", background: "var(--surface)", border: `1px solid ${loading ? "var(--ink-3)" : "var(--border-strong)"}`, borderRadius: 12, boxShadow: "0 1px 2px rgba(20,18,15,0.04)", transition: "border-color .15s" }}>
          <span style={{ color: loading ? "var(--ink)" : "var(--ink-3)", display: "flex" }}>
            {loading ? <SpinnerIcon size={16} /> : <Ic name="sparkle" size={16} />}
          </span>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && hasQuery) submit(q); }}
            placeholder={loading ? "Thinking across the firm's knowledge…" : "Ask anything across the firm's knowledge…"}
            disabled={loading}
            style={{ flex: 1, border: 0, outline: 0, background: "transparent", fontFamily: "var(--sans)", fontSize: 16, color: loading ? "var(--ink-3)" : "var(--ink)", letterSpacing: "-0.005em" }}
          />
          <span className="kbd">⌘K</span>
          <button disabled={!hasQuery} onClick={() => hasQuery && submit(q)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", background: hasQuery ? "var(--ink)" : "var(--raised)", color: hasQuery ? "var(--bg)" : "var(--ink-3)", border: 0, borderRadius: 7, fontFamily: "var(--sans)", fontSize: 12.5, fontWeight: 500, cursor: hasQuery ? "pointer" : "not-allowed", transition: "background .12s, color .12s" }}>
            {loading ? "Thinking…" : <><span>Ask</span> <Ic name="arrowR" size={11} /></>}
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
          <Chip on={mode === "library"} onClick={() => setMode("library")}><Ic name="knowledge" size={12} /> Library <span className="count">{themes.length}</span></Chip>
          <Chip on={mode === "chat"} onClick={() => setMode("chat")}><Ic name="chat" size={12} /> My questions <span className="count">{chats.filter(c => !c._loading).length}</span></Chip>
          <span style={{ flex: 1 }} />
          {mode === "library" && (
            <span style={{ display: "flex", gap: 5, alignItems: "center", color: "var(--ink-3)", fontSize: 11.5 }}>
              Try:
              {DEMO_QUESTIONS.slice(0, 3).map(dq => (
                <button key={dq} onClick={() => setQ(dq)} style={{ padding: "1px 8px", borderRadius: 999, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer", color: "var(--ink-2)", fontSize: 11.5, fontFamily: "var(--sans)" }}>{dq}</button>
              ))}
            </span>
          )}
        </div>
      </div>

      <div className="scroll" style={{ background: "var(--bg)" }}>
        {mode === "library"
          ? <LibraryView themes={themes} deals={deals} isAdmin={isAdmin} generating={generating} highlight={justSaved} onAsk={submit} onGenerate={onGenerate} onPublish={onPublish} onDelete={onDeleteTheme} onNew={() => { setThemeSeed(null); setThemeModal("new"); }} onEdit={(t) => { setThemeSeed(null); setThemeModal(t); }} />
          : <ChatView chats={chats} activeId={activeId} onPick={setActiveId} onSave={save} onFollowup={submit} onOpenDeal={onOpenDeal} onPromote={onPromote} onRate={rate} />}
      </div>

      {themeModal && (
        <ThemeModal theme={themeModal} deals={deals} promote={themeSeed}
          onClose={closeThemeModal} onSaved={onThemeSaved} />
      )}
    </div>
  );
}
